import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "bg-secondary-800 text-secondary-50 hover:bg-secondary-600/90 border-secondary-100/30 border",
        destructive: "bg-red-500 text-secondary-50 hover:bg-red-500/90",
        outline:
          "border border-secondary-200 bg-white hover:bg-secondary-100 hover:text-secondary-700",
        secondary:
          "bg-secondary-100 text-secondary-900 hover:bg-secondary-100/80",
        ghost: "hover:bg-secondary-100 hover:text-secondary-900",
        link: "text-secondary-900 underline-offset-4 hover:underline",
        obituary_success:
          "bg-primary-800 text-primary-50 hover:bg-primary-600/90 border-primary-100/30 border uppercase font-bold",
        obituary_cancel:
          "bg-tertiary-800 text-tertiary-50 hover:bg-tertiary-600/90 border-tertiary-100/30 border uppercase font-bold",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
